import React, { ReactElement } from "react";
import { Field, Step, TextBlock } from "@lib/shared/types";
import { DomainInfo } from "src/api/domainInfo";
import { DomainForm } from "src/api/domainForm";
import { Classes, FormProps } from "@components/shared/form";
import { useForm } from "../hooks/useForm";
import { InvalidFieldWithValue } from "../hooks";
import { FormStep } from "./FormStep";
import FormExtraSteps from "./FormExtraSteps";
import styles from "@components/shared/form/styles.module.scss";

type UseFormReturn = ReturnType<typeof useForm>;

export interface FormStepsWrapperProps {
    slideFormValue: number;
    steps: Step[];
    stepsConfig: FormProps["stepsConfig"];
    formProps: FormProps;
    // FormStep shared props
    isSecondServiceForm: boolean | undefined;
    operation: UseFormReturn["operation"];
    classes?: Classes;
    isFormInsideOffersModal: boolean;
    formErrors: UseFormReturn["formErrors"];
    handleFieldBlur: UseFormReturn["handleFieldBlur"];
    handleFieldChange: UseFormReturn["handleFieldChange"];
    currentStep: number;
    isLoading: boolean;
    globalLoading: boolean;
    formError: UseFormReturn["formError"];
    fieldsData: UseFormReturn["fieldsData"];
    visitedSteps: UseFormReturn["visitedSteps"];
    checkIfStepHasOneField: UseFormReturn["checkIfStepHasOneField"];
    formSubmitted: boolean;
    formId: number;
    finalForm: DomainForm;
    stepStatus: UseFormReturn["stepStatus"];
    form: DomainForm | null | undefined;
    updateSecondServices: FormProps["updateSecondServices"];
    tags: FormProps["tags"];
    activeSecondServices: FormProps["activeSecondServices"];
    secondServiceLoading: FormProps["secondServiceLoading"];
    showPiiCompletionOtherFields: UseFormReturn["showPiiCompletionOtherFields"];
    setShowPiiCompletionOtherFields: UseFormReturn["setShowPiiCompletionOtherFields"];
    searchableMultiSelect: FormProps["searchableMultiSelect"];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    finalAnswerSummaryConfigs: any;
    tcpa: React.JSX.Element | undefined;
    singleQuestion: FormProps["singleQuestion"];
    mergeInputWithSubmitOnFirstStep: FormProps["mergeInputWithSubmitOnFirstStep"];
    apiCallLoading: UseFormReturn["apiCallLoading"];
    // FormExtraSteps props
    formErrorsResolved: UseFormReturn["formErrorsResolved"];
    invalidFields: InvalidFieldWithValue[];
    isExpandable: (field: Field) => boolean;
    getExpandableFields: (field: Field) => Field[];
    domain: DomainInfo;
    domainNameTranslation: string;
    isLastStep: boolean;
    showOtp: boolean;
    otp: UseFormReturn["otp"];
    dispatch: UseFormReturn["dispatch"];
    submitButtonText: string | undefined;
    startSecondRoundBidding: UseFormReturn["startSecondRoundBidding"];
    goToThankYouPage: boolean | undefined;
    goToListPage: boolean | undefined;
    compactOfferBlock: unknown;
    hideOffers: boolean | undefined;
    customThankYouStep: FormProps["customThankYouStep"];
    colors: FormProps["colors"];
    isLoading2: boolean;
    disableAutoFocus?: boolean;
}

export default function FormStepsWrapper({
    slideFormValue,
    steps,
    stepsConfig,
    formProps,
    isSecondServiceForm,
    operation,
    classes,
    isFormInsideOffersModal,
    formErrors,
    handleFieldBlur,
    handleFieldChange,
    currentStep,
    isLoading,
    globalLoading,
    formError,
    fieldsData,
    visitedSteps,
    checkIfStepHasOneField,
    formSubmitted,
    formId,
    finalForm,
    stepStatus,
    form,
    updateSecondServices,
    tags,
    activeSecondServices,
    secondServiceLoading,
    showPiiCompletionOtherFields,
    setShowPiiCompletionOtherFields,
    searchableMultiSelect,
    finalAnswerSummaryConfigs,
    tcpa,
    singleQuestion,
    mergeInputWithSubmitOnFirstStep,
    apiCallLoading,
    formErrorsResolved,
    invalidFields,
    isExpandable,
    getExpandableFields,
    domain,
    domainNameTranslation,
    isLastStep,
    showOtp,
    otp,
    dispatch,
    submitButtonText,
    startSecondRoundBidding,
    goToThankYouPage,
    goToListPage,
    compactOfferBlock,
    hideOffers,
    customThankYouStep,
    colors,
    isLoading2,
    disableAutoFocus,
}: FormStepsWrapperProps): ReactElement {
    return (
        <div
            style={{ transform: `translate(${slideFormValue}%, 0)` }}
            className={`${styles["steps-wrapper"]} ltrStyle`}
            id="stepsWrapper"
        >
            {steps?.map((step, stepIndex) => (
                <FormStep
                    isSecondServiceForm={isSecondServiceForm}
                    operation={operation}
                    formButtonClassName={classes?.formButtonClassName}
                    isFormInsideOffersModal={isFormInsideOffersModal}
                    formErrors={formErrors}
                    key={stepIndex}
                    step={step}
                    index={stepIndex}
                    handleFieldBlur={handleFieldBlur}
                    handleFieldChange={handleFieldChange}
                    currentStep={currentStep}
                    isLoading={isLoading || globalLoading}
                    formError={formError}
                    fieldsData={fieldsData}
                    steps={steps}
                    visitedSteps={visitedSteps}
                    {...formProps}
                    hideStepTitle={stepsConfig?.[stepIndex]?.hideTitle ?? false}
                    hideStepDescription={
                        stepsConfig?.[stepIndex]?.hideDescription ?? false
                    }
                    forceStepTitle={
                        stepsConfig?.[stepIndex]?.forceStepTitle ?? false
                    }
                    checkIfStepHasOneField={
                        checkIfStepHasOneField as (fieldsData: {
                            [x: string]: {
                                value: string;
                                valid: boolean;
                                errorMessage: string;
                            };
                        }) => boolean
                    }
                    formSubmitted={formSubmitted}
                    formId={formId}
                    form={finalForm}
                    overrideStepTitle={stepsConfig?.[stepIndex]?.title ?? ""}
                    overrideStepDescription={
                        stepsConfig?.[stepIndex]?.description ?? ""
                    }
                    stepStatus={stepStatus}
                    textBlocks={
                        (form?.textBlocks ?? []) as unknown as TextBlock[]
                    }
                    updateSecondServices={updateSecondServices}
                    tags={tags}
                    activeSecondServices={activeSecondServices}
                    secondServiceLoading={secondServiceLoading}
                    showPiiCompletionOtherFields={showPiiCompletionOtherFields}
                    setShowPiiCompletionOtherFields={
                        setShowPiiCompletionOtherFields
                    }
                    searchableMultiSelect={searchableMultiSelect}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    answerSummaryConfigs={finalAnswerSummaryConfigs}
                    globalLoading={globalLoading}
                    tcpa={tcpa}
                    singleQuestion={singleQuestion}
                    mergeInputWithSubmitOnFirstStep={
                        mergeInputWithSubmitOnFirstStep
                    }
                    apiCallLoading={apiCallLoading}
                    disableAutoFocus={disableAutoFocus}
                />
            ))}
            <FormExtraSteps
                formErrors={formErrors}
                formErrorsResolved={formErrorsResolved}
                invalidFields={invalidFields}
                isExpandable={isExpandable}
                getExpandableFields={getExpandableFields}
                domain={domain}
                domainNameTranslation={domainNameTranslation}
                isLastStep={isLastStep}
                currentStep={currentStep}
                showOtp={showOtp}
                otp={otp}
                dispatch={dispatch}
                submitButtonText={submitButtonText}
                startSecondRoundBidding={startSecondRoundBidding}
                formSubmitted={formSubmitted}
                goToThankYouPage={goToThankYouPage}
                goToListPage={goToListPage}
                compactOfferBlock={compactOfferBlock}
                hideOffers={hideOffers}
                customThankYouStep={customThankYouStep}
                fieldsData={fieldsData}
                finalForm={finalForm}
                handleFieldBlur={handleFieldBlur}
                handleFieldChange={handleFieldChange}
                stepStatus={stepStatus}
                isSecondServiceForm={isSecondServiceForm}
                formProps={formProps}
                classes={classes}
                colors={colors}
                isLoading={isLoading2}
                operation={operation}
                searchableMultiSelect={searchableMultiSelect}
                singleQuestion={singleQuestion}
            />
        </div>
    );
}
